import { useState, useEffect } from "react";
import PanelRefreshButton from "./PanelRefreshButton";
import { API, axios } from "@/api";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, Shield, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const SEVERITY_COLORS = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-slate-100 text-slate-600 border-slate-200",
};

export default function QuarantineQueuePanel() {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [acting, setActing] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try {
      const [sumRes, qRes] = await Promise.all([
        axios.get(`${API}/admin/quarantine/summary`),
        axios.get(`${API}/admin/quarantine/queue?limit=50`),
      ]);
      setSummary(sumRes.data);
      setRecords(qRes.data.records || []);
      setLastRefreshedAt(Date.now());
    } catch { setSummary(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const handleApprove = async (bid, qid) => {
    setActing(qid);
    try {
      await axios.post(`${API}/admin/quarantine/approve/${bid}/${qid}`);
      toast.success("Update approved and applied");
      fetch();
    } catch { toast.error("Approve failed"); }
    finally { setActing(null); }
  };

  const handleReject = async (bid, qid) => {
    setActing(qid);
    try {
      const res = await axios.post(`${API}/admin/quarantine/reject/${bid}/${qid}`);
      const d = res.data;
      toast.success(`Rejected. Source ${d.source_name} errors: ${d.source_error_count}${d.source_deprioritized ? " (AUTO-DEPRIORITIZED)" : ""}`);
      fetch();
    } catch { toast.error("Reject failed"); }
    finally { setActing(null); }
  };

  const handleAutoApprove = async () => {
    setActing("auto");
    try {
      const res = await axios.post(`${API}/admin/quarantine/auto-approve-low`);
      toast.success(`Auto-approved ${res.data.auto_approved} low-severity updates`);
      fetch();
    } catch { toast.error("Auto-approve failed"); }
    finally { setActing(null); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;

  return (
    <div className="space-y-4" data-testid="quarantine-queue-panel">
      <div className="flex justify-end -mb-2"><PanelRefreshButton onRefresh={fetch} lastRefreshedAt={lastRefreshedAt} testId="quarantine-refresh" /></div>
      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-3 gap-3" data-testid="quarantine-summary">
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-center">
            <AlertTriangle className="w-4 h-4 text-amber-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-amber-700" data-testid="quarantine-pending-count">{summary.pending}</p>
            <p className="text-[10px] uppercase tracking-wider text-amber-500 font-semibold">Pending</p>
          </div>
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3 text-center">
            <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-emerald-700">{summary.approved}</p>
            <p className="text-[10px] uppercase tracking-wider text-emerald-500 font-semibold">Approved</p>
          </div>
          <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-center">
            <XCircle className="w-4 h-4 text-red-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-red-600">{summary.rejected}</p>
            <p className="text-[10px] uppercase tracking-wider text-red-400 font-semibold">Rejected</p>
          </div>
        </div>
      )}

      {/* Auto-approve button */}
      {records.length > 0 && (
        <button
          className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 text-white text-xs font-medium rounded-lg hover:bg-emerald-600 transition-colors disabled:opacity-50"
          onClick={handleAutoApprove}
          disabled={acting === "auto"}
          data-testid="auto-approve-low-btn"
        >
          {acting === "auto" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
          Auto-approve all low severity
        </button>
      )}

      {/* Queue */}
      {records.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-4" data-testid="quarantine-empty">No records in quarantine</p>
      ) : (
        <div className="space-y-2 max-h-[500px] overflow-y-auto" data-testid="quarantine-records-list">
          {records.map((r) => {
            const isExp = expanded === r.quarantine_id;
            const maxSeverity = r.anomalies.reduce((max, a) => {
              const order = { high: 3, medium: 2, low: 1 };
              return (order[a.severity] || 0) > (order[max] || 0) ? a.severity : max;
            }, "low");
            return (
              <div key={r.quarantine_id} className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden" data-testid={`quarantine-item-${r.quarantine_id}`}>
                {/* Header */}
                <div className="flex items-center gap-3 p-3 cursor-pointer" onClick={() => setExpanded(isExp ? null : r.quarantine_id)}>
                  <div className={`px-1.5 py-0.5 text-[10px] font-bold rounded border ${SEVERITY_COLORS[maxSeverity]}`}>
                    {maxSeverity.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{r.business_name}</p>
                    <p className="text-[11px] text-slate-400">{r.anomalies.length} anomal{r.anomalies.length === 1 ? "y" : "ies"} &middot; {r.source_name}</p>
                  </div>
                  <div className="flex gap-1">
                    <button className="p-1.5 rounded-lg bg-emerald-100 text-emerald-600 hover:bg-emerald-200 disabled:opacity-40"
                      onClick={(e) => { e.stopPropagation(); handleApprove(r.business_id, r.quarantine_id); }}
                      disabled={acting === r.quarantine_id} title="Approve" data-testid={`approve-btn-${r.quarantine_id}`}>
                      {acting === r.quarantine_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    </button>
                    <button className="p-1.5 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 disabled:opacity-40"
                      onClick={(e) => { e.stopPropagation(); handleReject(r.business_id, r.quarantine_id); }}
                      disabled={acting === r.quarantine_id} title="Reject" data-testid={`reject-btn-${r.quarantine_id}`}>
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                    {isExp ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </div>
                {/* Expanded: side-by-side comparison */}
                {isExp && (
                  <div className="border-t border-slate-200 p-3 space-y-2" data-testid={`quarantine-detail-${r.quarantine_id}`}>
                    {r.anomalies.map((a, i) => (
                      <div key={i} className={`p-2 rounded-lg border text-xs ${SEVERITY_COLORS[a.severity]}`}>
                        <p className="font-semibold">{a.type} <span className="font-normal opacity-70">({a.severity})</span></p>
                        <p className="opacity-80 mt-0.5">{a.detail}</p>
                        {a.old_value && <p className="mt-1"><span className="font-medium">Existing:</span> {a.old_value}</p>}
                        {a.new_value && <p><span className="font-medium">New:</span> {a.new_value}</p>}
                      </div>
                    ))}
                    {/* Side-by-side data */}
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div>
                        <p className="text-[10px] font-bold text-slate-500 uppercase mb-1">Existing Data</p>
                        {Object.entries(r.existing_data || {}).filter(([k]) => !["business_id"].includes(k)).slice(0, 8).map(([k, v]) => (
                          <p key={k} className="text-[11px] text-slate-500"><span className="text-slate-400">{k}:</span> {String(v || "—").slice(0, 40)}</p>
                        ))}
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-amber-600 uppercase mb-1">New Data</p>
                        {Object.entries(r.new_data || {}).slice(0, 8).map(([k, v]) => (
                          <p key={k} className="text-[11px] text-amber-700"><span className="text-amber-500">{k}:</span> {String(v || "—").slice(0, 40)}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
